import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase.ts';
import { useStaff } from '../../contexts/StaffContext.tsx';
import type { MenuSnapshot } from '../../lib/types.ts';
import { formatMoney } from '../../lib/format.ts';

async function fetchMenuSnapshot(slug: string): Promise<MenuSnapshot> {
  const { data, error } = await supabase.rpc('get_menu_snapshot', { p_slug: slug });
  if (error) throw error;
  return data as MenuSnapshot;
}

export function StopListPage() {
  const { activeMembership } = useStaff();
  const queryClient = useQueryClient();

  const tenantSlug = activeMembership?.tenant_slug ?? '';
  const locationLocalId = activeMembership?.locations?.[0]?.local_id ?? '';

  const { data, isLoading } = useQuery({
    queryKey: ['menu', tenantSlug],
    queryFn: () => fetchMenuSnapshot(tenantSlug),
    enabled: !!tenantSlug,
    staleTime: 60_000,
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ itemLocalId, stop }: { itemLocalId: string; stop: boolean }) => {
      const { error } = await supabase.rpc('staff_toggle_stop_list', {
        p_tenant_slug: tenantSlug,
        p_location_local_id: locationLocalId,
        p_item_local_id: itemLocalId,
        p_stop: stop,
        p_until: null,
        p_reason: null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu', tenantSlug] });
      queryClient.invalidateQueries({ queryKey: ['kitchen-orders'] });
    },
  });

  if (!activeMembership) return null;

  if (isLoading || !data) {
    return (
      <div className="p-4 flex flex-col gap-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-14 animate-pulse rounded-xl" style={{ background: 'var(--surface)' }} />
        ))}
      </div>
    );
  }

  const allItems = data.categories.flatMap((c) =>
    c.items.map((item) => ({ ...item, category_name: c.name })),
  );

  return (
    <div className="flex min-h-dvh flex-col" style={{ background: 'var(--bg)' }}>
      <header
        className="sticky top-0 z-20 px-4 py-3"
        style={{ background: 'var(--surface)', boxShadow: 'var(--shadow-sm)' }}
      >
        <h1 className="text-base font-semibold" style={{ color: 'var(--text)' }}>
          Стоп-лист
        </h1>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {data.tenant.name} · {data.location.name}
        </p>
      </header>

      <main className="flex-1 px-4 py-3 flex flex-col gap-2">
        {allItems.length === 0 && (
          <p className="text-center pt-12 text-sm" style={{ color: 'var(--text-muted)' }}>
            Нет позиций в меню
          </p>
        )}
        {allItems.map((item) => {
          const inStop = item.stop_list;
          const pending = toggleMutation.isPending;

          return (
            <div
              key={item.item_local_id}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5"
              style={{
                background: 'var(--surface)',
                boxShadow: 'var(--shadow-sm)',
                opacity: pending ? 0.7 : 1,
              }}
            >
              {/* Name + category */}
              <div className="flex-1 min-w-0">
                <p
                  className="text-sm font-medium truncate"
                  style={{ color: inStop ? 'var(--text-muted)' : 'var(--text)' }}
                >
                  {item.name}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {item.category_name} · {formatMoney(item.price, data.tenant.currency)}
                </p>
              </div>

              {/* Stop indicator */}
              {inStop && (
                <span
                  className="rounded-full px-2 py-0.5 text-xs font-medium flex-shrink-0"
                  style={{ background: 'oklch(97% 0.015 25)', color: 'var(--danger)' }}
                >
                  Стоп
                </span>
              )}

              {/* Toggle */}
              <button
                onClick={() =>
                  toggleMutation.mutate({ itemLocalId: item.item_local_id, stop: !inStop })
                }
                disabled={pending}
                aria-label={inStop ? `Убрать из стоп-листа: ${item.name}` : `Добавить в стоп-лист: ${item.name}`}
                aria-pressed={inStop}
                className="flex-shrink-0 relative rounded-full transition-colors"
                style={{
                  width: 44,
                  height: 26,
                  background: inStop ? 'var(--danger)' : 'var(--n-300)',
                  minWidth: 44,
                  minHeight: 26,
                }}
              >
                <span
                  className="absolute rounded-full bg-white shadow transition-transform"
                  style={{
                    width: 20,
                    height: 20,
                    top: 3,
                    left: inStop ? 'calc(100% - 23px)' : 3,
                  }}
                />
              </button>
            </div>
          );
        })}
      </main>
    </div>
  );
}
